//@flow

import React, { useState, Fragment } from 'react';
import Rater from 'react-rater';
import orderBy from 'lodash/orderBy';
import reverse from 'lodash/reverse';
import startCase from 'lodash/startCase';
import isEmpty from 'lodash/isEmpty';
import capitalize from 'lodash/capitalize';
import { Col, Button } from 'reactstrap';
import { camelizeKeys } from 'humps';

import seo from '../../../constants/seo';
import api from '../../../lib/api';
import config from '../../../config';
import makeUrl from '../../../lib/makeUrl';
import { Loading, Page } from '../../../components';
import SortDropdown from './SortDropdown';
import { useEffectOnFirstAndUpdateRender } from '../../../hooks';
import FeaturedLodges from './FeaturedLodges';
import {
  getFromCookies,
  provideIcon,
  getDiffInDays,
  showPriceAndRooms,
} from '../helpers';

type Props = { placeId: string, lodgeId?: string, placeName: string, region: string };

const cached = new Map();
const DEFAULT_ROOM_CAPACITY = 2;

export default function LodgeLists(props: Props) {
  const { placeId, lodgeId } = props;
  const [lodges, setLodges] = useState(null);
  const [order, setOrder] = useState('desc');
  const [sortKey, setSortKey] = useState('ratings');

  useEffectOnFirstAndUpdateRender(fetchLodges, [placeId]);

  async function fetchLodges() {
    const cachedData = cached.get(placeId);
    if (cachedData) {
      setLodges(cachedData);
      return;
    }

    const { data } = await api.get(`lodgings/lodges?place=${placeId}&is_bookable=1`);

    setLodges(camelizeKeys(data));
    cached.set(placeId, camelizeKeys(data));
  }

  const { checkinDate, checkoutDate, pax } = getFromCookies();
  const numberOfDays: number = getDiffInDays(checkinDate, checkoutDate);

  const placeName = props.placeName
    ? props.placeName
    : isEmpty(lodges)
      ? ''
      : lodges[0].address;

  const regionName = props.region ? capitalize(props.region) : 'Everest';
  const title = `Lodges In ${startCase(placeName)}, ${capitalize(regionName)} Region`;

  return (
    <Col xs="12" lg="12" xl="9">
      {lodges ? (
        <Page {...seo.lodgeListings(startCase(placeName))}>
          {lodges.length > 0 && <FeaturedLodges placeId={placeId} lodges={lodges} placeName={placeName} />}
          <div className="search-title">
            <h1 className="search-result-title">{title}</h1>
            <SortDropdown sortKey={sortKey} order={order} updateSortKey={setSortKey} updateOrder={setOrder} />
          </div>
          {lodges.length > 0 ? (
            sortLodges(lodges, lodgeId, order, sortKey).map(lodge => {
              const {
                id,
                name,
                region,
                address,
                coverImages,
                startingPrice,
                averageRating,
                reviews,
                isRecommended,
              } = lodge;
              let { includedServices } = lodge;

              if (config.blockedLodgeIds.includes(id)) return null;

              // Remove breakfast filter in new update
              // includedServices = includedServices && includedServices.filter && includedServices.filter(s => s !== 'Breakfast');

              const amount = startingPrice && startingPrice.amount;
              const roomRate = amount ? (Number(amount)) * DEFAULT_ROOM_CAPACITY : 0;
              const numRooms = Math.ceil(Number(pax) / DEFAULT_ROOM_CAPACITY);
              const startingPriceAmount = Math.ceil(
                (numRooms * roomRate) * Number(numberOfDays)
              ).toLocaleString();

              return (
                <a
                  href={makeUrl.goToLodgeDetails(name, id, address)}
                  target="_blank"
                  key={id}
                  id="lodges-select-room-btn"
                >
                  <div className="search__card lodge" id="lodges-select-room-btn">
                    <section className="lodge__image" id="lodges-select-room-btn">
                      {isRecommended && <div className="ribbon">Recommended</div>}
                      <img
                        alt={name}
                        src={`${config.RESOURCE_URL}/${coverImages.medium}`}
                        id="lodges-select-room-btn"
                      />
                    </section>

                    <section className="lodge__information" id="lodges-select-room-btn">
                      <div className="lodge_name_location" id="lodges-select-room-btn">
                        <h3 className="lodge_name" id="lodges-select-room-btn">{name}</h3>
                        <p className="lodge_location" id="lodges-select-room-btn">
                          {address}, {region}
                        </p>
                        <div className="lodge_rating">
                          <Rater total={5} rating={averageRating} interactive={false} />
                          <p>{(reviews && reviews.numberOfReviews) || 0} Reviews</p>
                        </div>
                      </div>
                      {!isEmpty(includedServices) && (
                        <div className="lodge_included" id="lodges-select-room-btn">
                          <ul>
                            {includedServices.map(s => (
                              <li key={s}>
                                <i className={provideIcon(s)} />
                                {capitalize(s)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="lodge_highlight_service d-none d-md-block">
                        <ul>
                          <li>*2 Days Flexibility</li>
                          <li>*Free 24 hour cancellation</li>
                          <li>*Includes all taxes</li>
                        </ul>
                      </div>
                    </section>

                    <section className="lodge__summary" id="lodges-select-room-btn">
                      <div className="lodge_rating">
                        <Rater total={5} rating={averageRating} interactive={false} />
                        <p>{(reviews && reviews.numberOfReviews) || 0} Reviews</p>
                      </div>
                      <span className="lodge_price_person">
                        {showPriceAndRooms(numberOfDays, numRooms)}
                      </span>
                      {startingPrice && (
                        <Fragment>
                          <p className="lodge_info_cost">
                            {startingPrice.currency}{' '}
                            {startingPriceAmount}
                          </p>
                        </Fragment>
                      )}
                      <div className="lodge_highlight_service" id="lodges-select-room-btn">
                        <p>{startingPrice ? '' : 'Cannot be Booked'}</p>
                      </div>
                      <Button color="yellow" id="lodges-select-room-btn" >{startingPrice ? 'Select Room' : 'View Details'}</Button>
                    </section>
                    
                    <div className="mobile-display d-block d-md-none">
                      <div className="lodge_highlight_service">
                        <ul>
                          <li>*2 Days Flexibility</li>
                          <li>*Free 24 hour cancellation</li>
                          <li>*Includes all taxes</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </a>
              );
            })
          ) : (
              <p>Sorry! No Lodges were found</p>
            )}
        </Page>
      ) : (
          <Loading />
        )}
    </Col>
  );
}

function sortLodges(lodges, lodgeId, order, sortKey) {
  let sortedLodges = sortKey === 'price'
    ? orderBy(lodges, [l => l.startingPrice && Number(l.startingPrice.amount)], [order])
    : reverse(orderBy(lodges, [l => l.averageRating && (Number(l.averageRating) * -1)], [order]));

  if (lodgeId) {
    const choosenLodge = sortedLodges.filter(l => l.id === lodgeId);
    sortedLodges = [...choosenLodge, ...sortedLodges.filter(l => l.id !== lodgeId)];
  }

  return sortedLodges;
}



// WEBPACK FOOTER //
// ./src/screens/Lodge/Listings/LodgeLists.js